import React, { useMemo, useState } from 'react';

import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { getCountryObjs } from '@luxuryescapes/lib-countries';

import GridPagination from '~/components/Common/Elements/GridPagination';
import ForcedRankingsConfigForm from '~/components/ForcedRankingsConfig/ForcedRankingsConfigForm';

import { ForcedRankingsConfig } from '~/services/PublicOfferFeedService';

import dateFormatter from '~/utils/dateFormatter';

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 0.5,
};

const getCountryOptions = () => {
  const countries = getCountryObjs();
  return Object.values(countries)
    .map((country) => ({
      code: country.iso2,
      label: `${country.countryName} (${country.iso2})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const countryOptions = getCountryOptions();

const getColumns = ({
  onEdit,
  onDelete,
}: {
  onEdit: (row: ForcedRankingsConfig) => void;
  onDelete: (row: ForcedRankingsConfig) => void;
}): Array<GridColDef> => [
  {
    field: 'user_regions',
    headerName: 'Website Regions',
    ...sharedOpts,
    renderCell: (params) => <>{params.row.user_regions?.join(', ')}</>,
  },
  { field: 'country_code', headerName: "Offer's Country", ...sharedOpts },
  { field: 'distance_lookup', headerName: 'Distance Lookup', ...sharedOpts },
  {
    field: 'created_at',
    headerName: 'Created At',
    ...sharedOpts,
    renderCell: (params) => <>{dateFormatter(params.row.created_at)}</>,
  },
  {
    field: 'updated_at',
    headerName: 'Updated At',
    ...sharedOpts,
    renderCell: (params) => <>{dateFormatter(params.row.updated_at)}</>,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    ...sharedOpts,
    renderCell: (params) => (
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" size="small" onClick={() => onEdit(params.row)}>
          Edit
        </Button>
        <Button variant="outlined" size="small" onClick={() => onDelete(params.row)}>
          Delete
        </Button>
      </Stack>
    ),
  },
];

type Props = {
  countryCode: string;
  setCountryCode: (value: string) => void;
  distanceLookup: '10km' | '100km' | '1000km' | 'country_radius' | '';
  setDistanceLookup: (value: '10km' | '100km' | '1000km' | 'country_radius' | '') => void;

  data: Array<ForcedRankingsConfig>;
  isLoading: boolean;
  totalCount: number;
  pageNum: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;

  handleOpen: () => void;
  handleEditConfig: (config: ForcedRankingsConfig) => void;
  handleDeleteConfig: (config: ForcedRankingsConfig) => void;

  open: boolean;
  setOpen: (open: boolean) => void;
  editableConfig: ForcedRankingsConfig | null;
  fetchData: () => void;
};

export default function ForcedRankingsConfigTab({
  countryCode,
  setCountryCode,
  distanceLookup,
  setDistanceLookup,
  data,
  isLoading,
  totalCount,
  pageNum,
  pageSize,
  onPageChange,
  handleOpen,
  handleEditConfig,
  handleDeleteConfig,
  open,
  setOpen,
  editableConfig,
  fetchData,
}: Props) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<ForcedRankingsConfig | undefined>(undefined);

  // Find the selected country option based on the countryCode prop
  const selectedCountryOption = countryOptions.find((option) => option.code === countryCode) || null;

  const rows = useMemo(() => data.map((row, index) => ({ id: index, ...row })), [data]);

  const handleDeleteClick = (config: ForcedRankingsConfig) => {
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (configToDelete) {
      handleDeleteConfig(configToDelete);
      setDeleteDialogOpen(false);
      setConfigToDelete(undefined);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setConfigToDelete(undefined);
  };

  return (
    <>
      <Grid container spacing={2} justifyContent="flex-end" alignItems="end">
        <Grid size={8} container spacing={2}>
          <Grid size={6}>
            <Autocomplete
              id="country_code"
              options={countryOptions}
              getOptionLabel={(option) => option.label}
              value={selectedCountryOption}
              onChange={(_, newValue) => {
                setCountryCode(newValue?.code || '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Offer Country"
                  variant="outlined"
                  placeholder="Search countries..."
                  fullWidth
                />
              )}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              renderOption={(props, option) => (
                <li {...props} key={option.code}>
                  {option.label}
                </li>
              )}
            />
          </Grid>
          <Grid size={6}>
            <Select
              id="distance_lookup"
              displayEmpty
              value={distanceLookup}
              onChange={(e) => setDistanceLookup(e.target.value as '10km' | '100km' | '1000km' | 'country_radius' | '')}
              fullWidth
            >
              <MenuItem value="">All Distance Lookups</MenuItem>
              <MenuItem value="10km">10km</MenuItem>
              <MenuItem value="100km">100km</MenuItem>
              <MenuItem value="1000km">1000km</MenuItem>
              <MenuItem value="country_radius">Country Radius</MenuItem>
            </Select>
          </Grid>
        </Grid>
        <Grid size={4}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="contained" color="primary" onClick={handleOpen}>
              New Config
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <ForcedRankingsConfigForm open={open} setOpen={setOpen} fetchData={fetchData} editableConfig={editableConfig} />

      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the forced rankings configuration for Country Code:{' '}
            {configToDelete?.country_code}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} variant="text">
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="primary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={rows}
          columns={getColumns({ onEdit: handleEditConfig, onDelete: handleDeleteClick })}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
        />
      </Box>
    </>
  );
}
